import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, ExternalLink } from 'lucide-react';
import { CORRIDORS, STATUS_COLORS } from '@/constants/laadplein-data';
import type { LaadpleinLocatie, LocatieStatus } from '@/types/laadplein';

type Props = {
  locaties: LaadpleinLocatie[];
  onAdd: (locatie: Omit<LaadpleinLocatie, 'id' | 'totaal' | 'spula' | 'corridor_data'>) => void;
  onRemove: (id: string) => void;
  isLoading?: boolean;
  isAdding?: boolean;
};

const INITIAL_FORM = {
  naam: '', adres: '', postcode: '', corridor: 'a2-north',
  oppervlakte: 3000, net_kw: 1750, afstand_weg: 0.5, status: 'prospect' as LocatieStatus,
  s_bereik: 5, s_net: 5, s_concur: 5, s_vraag: 5, s_kosten: 5, notities: '',
};

const SCORE_LABELS = [
  { key: 's_bereik', label: 'Bereikb.' },
  { key: 's_net', label: 'Net' },
  { key: 's_concur', label: 'Concurr.' },
  { key: 's_vraag', label: 'Vraag' },
  { key: 's_kosten', label: 'Kosten' },
] as const;

export function LocatiesTab({ locaties, onAdd, onRemove, isLoading, isAdding }: Props) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const navigate = useNavigate();

  const handleSave = () => {
    if (!form.naam.trim()) return;
    onAdd(form);
    setForm(INITIAL_FORM);
    setShowForm(false);
  };

  const sorted = [...locaties].sort((a, b) => (b.totaal || 0) - (a.totaal || 0));

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-gray-500">
          {isLoading ? 'Laden...' : `${locaties.length} locatie(s) beoordeeld`}
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700">
          + Locatie scoren
        </Button>
      </div>

      {showForm && (
        <Card className="mb-4 bg-gray-50">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
              <div>
                <Label className="text-xs text-gray-500">Naam</Label>
                <Input value={form.naam} onChange={e => setForm({ ...form, naam: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Adres</Label>
                <Input value={form.adres} onChange={e => setForm({ ...form, adres: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Postcode</Label>
                <Input value={form.postcode} onChange={e => setForm({ ...form, postcode: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Dichtstbijzijnde corridor</Label>
                <Select value={form.corridor} onValueChange={v => setForm({ ...form, corridor: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CORRIDORS.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.weg} {c.traject} ({c.trucks_dag.toLocaleString()} tr/dag)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Oppervlakte (m²)</Label>
                <Input type="number" value={form.oppervlakte} onChange={e => setForm({ ...form, oppervlakte: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Netaansluiting (kW)</Label>
                <Input type="number" value={form.net_kw} onChange={e => setForm({ ...form, net_kw: Number(e.target.value) })} />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Afstand A/N-weg (km)</Label>
                <Input type="number" step="0.1" value={form.afstand_weg} onChange={e => setForm({ ...form, afstand_weg: Number(e.target.value) })} />
              </div>
            </div>

            <div className="text-xs font-medium text-gray-600 mb-2">Scores (1-10)</div>
            <div className="grid grid-cols-5 gap-3 mb-3">
              {SCORE_LABELS.map(({ key, label }) => (
                <div key={key}>
                  <Label className="text-xs text-gray-500">{label}</Label>
                  <Slider
                    min={1} max={10} step={1}
                    value={[form[key]]}
                    onValueChange={([v]) => setForm({ ...form, [key]: v })}
                    className="my-2"
                  />
                  <div className="text-center text-sm font-semibold text-emerald-700">{form[key]}</div>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={isAdding} className="bg-emerald-600 hover:bg-emerald-700">
                {isAdding && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Opslaan
              </Button>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Annuleren</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {sorted.map(loc => (
        <Card
          key={loc.id}
          className="mb-2 cursor-pointer hover:border-emerald-300 transition-colors"
          onClick={() => navigate(`/crm/laadplein/locatie/${loc.id}`)}
        >
          <CardContent className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <div className="font-semibold flex items-center gap-2">
                  {loc.naam}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[loc.status] || "bg-gray-100 text-gray-600"}`}>
                    {loc.status}
                  </span>
                  <ExternalLink className="h-3 w-3 text-gray-400" />
                </div>
                <div className="text-xs text-gray-500">
                  {loc.adres} &middot; {loc.postcode} &middot; {loc.oppervlakte?.toLocaleString()} m² &middot; {loc.net_kw} kW
                </div>
                {loc.corridor_data && (
                  <div className="text-xs text-blue-600 mt-1">
                    {loc.corridor_data.weg} {loc.corridor_data.traject}: {loc.corridor_data.trucks_dag.toLocaleString()} trucks/dag
                  </div>
                )}
                {loc.spula && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full mt-1 inline-block">
                    SPULA eligible
                  </span>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-emerald-600">{(loc.totaal || 0).toFixed(1)}</div>
                <div className="text-xs text-gray-400">/10</div>
                <button
                  onClick={e => { e.stopPropagation(); onRemove(loc.id); }}
                  className="text-xs text-red-400 mt-1 hover:text-red-600"
                >
                  Verwijder
                </button>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-1 mt-2">
              {SCORE_LABELS.map(({ key, label }) => {
                const v = loc[key] || 0;
                return (
                  <div key={key} className="text-center">
                    <div className="text-xs text-gray-400">{label}</div>
                    <div className="h-1.5 bg-gray-200 rounded-full mt-0.5">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${v * 10}%` }} />
                    </div>
                    <div className="text-xs font-medium">{v}</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
